// src/pages/locations/cities/EditCityPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useCity,
  useCountries,
  useRegions,
  useUpdateCityMutation,
} from "@/api/features/locations";
import CityForm from "@/components/locations/forms/CityForm";

import LocationFormPage from "../components/LocationFormPage";
import { useLeaveConfirm } from "../components/form/useLeaveConfirm";
import { useLocationScope } from "../useLocationScope";
import { apiErrorSummary } from "../formErrors";
import { locationCreatePath, locationTabPath } from "../locations.access";
import { withFlash } from "../flash";

const EditCityPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const id = Number(rawId);

  const { scope } = useLocationScope();
  const backTo = locationTabPath("cities", scope);

  const { data: city, isLoading, error: loadError } = useCity(id);

  // A city cannot change country, so the record itself decides which regions
  // are selectable — not whatever the list happened to be filtered by.
  const cityCountry = String(city?.countryIso2 ?? "").toUpperCase();

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });
  const countries = countriesRes?.items ?? [];

  const { data: regionsRes, isLoading: regionsLoading } = useRegions(
    cityCountry
      ? { countryIso2: cityCountry, page: 1, limit: 100 }
      : { countryIso2: "" }
  );
  const regions = regionsRes?.items ?? [];

  const updateMutation = useUpdateCityMutation();
  const { requestCancel, dialog } = useLeaveConfirm(() => navigate(backTo));

  const error =
    !Number.isFinite(id) || id <= 0
      ? "No city was specified."
      : apiErrorSummary(loadError);

  return (
    <LocationFormPage
      entity="cities"
      title="Edit city"
      subtitle={city?.name}
      backTo={backTo}
      backLabel="Back to Cities"
      isLoading={isLoading}
      loadingLabel="Loading city…"
      error={error}
    >
      {city && (
        <>
          <CityForm
            mode="edit"
            value={city}
            countries={countries}
            countriesLoading={countriesLoading}
            regions={regions}
            regionsLoading={Boolean(cityCountry) && regionsLoading}
            addRegionHref={locationCreatePath("regions", { country: cityCountry })}
            isSubmitting={updateMutation.isPending}
            error={updateMutation.error}
            onCancel={requestCancel}
            onSubmit={(values) =>
              updateMutation.mutate(
                {
                  id: city.id,
                  body: {
                    regionId: values.regionId ? Number(values.regionId) : null,
                    name: values.name,
                    nameNorm: values.nameNorm || null,
                    latitude: values.latitude || null,
                    longitude: values.longitude || null,
                  },
                },
                {
                  onSuccess: (updated) =>
                    navigate(backTo, withFlash(`${updated.name} was updated.`)),
                }
              )
            }
          />

          {dialog}
        </>
      )}
    </LocationFormPage>
  );
};

export default EditCityPage;
